// Shared look for "Grow your community". Every value mirrors an existing
// surface: cards/titles/pills from the CM dashboard (Dashboard/styles),
// inputs from InputField, tabs from CustomFilterTabs, primary button from the
// dashboard's withdraw button.
export const CARD_BORDER = "1px solid rgba(234, 234, 234, 1)";
export const INPUT_BORDER = "rgba(230, 228, 235, 1)";
export const TEXT_MAIN = "rgba(47, 43, 61, 0.9)";
export const TEXT_BODY = "rgba(62, 63, 76, 1)";
export const TEXT_SUB = "rgba(71, 84, 103, 1)";
export const TEXT_MUTED = "rgba(47, 43, 61, 0.4)";
export const GREEN = "rgba(37, 124, 109, 1)";
export const GREEN_HOVER = "rgba(30, 109, 91, 1)";
export const GREEN_ACCENT = "rgba(36, 139, 112, 1)";
export const GREEN_TINT = "rgba(219, 240, 236, 1)";
export const AMBER = "rgba(180, 120, 20, 1)";
export const AMBER_TINT = "rgba(255, 244, 229, 1)";
export const PURPLE = "#7367F0";
export const PURPLE_TINT = "rgba(242, 243, 251, 1)";
export const SOFT_BG = "rgba(248, 247, 250, 1)";
export const HOVER_BG = "rgba(245, 246, 250, 1)";

export const g = {
  page: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: { xs: "75px", md: "0px" },
    paddingBottom: "24px",
    maxWidth: "100%",
  },
  card: {
    border: CARD_BORDER,
    borderRadius: "12px",
    backgroundColor: "#fff",
    padding: "16px",
    minWidth: 0,
  },
  // The three columns share one top structure — header (title + up to two
  // subtitle lines) and a single control row — at fixed heights, so the page
  // reads across in straight lines.
  cardHeader: { display: "flex", flexDirection: "column", gap: "2px", minHeight: "67px", marginBottom: "8px" },
  // A size container, so a badge on the title line can shorten itself when the
  // card is narrow (see the share column's reset badge).
  cardTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    minWidth: 0,
    containerType: "inline-size",
  },
  cardTitle: { fontSize: "16px", fontWeight: 500, lineHeight: "160%", color: TEXT_MAIN, whiteSpace: "nowrap" },
  cardSub: {
    fontSize: "13px",
    color: TEXT_SUB,
    lineHeight: 1.5,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  controlRow: { height: "40px", display: "flex", alignItems: "center", marginBottom: "12px", minWidth: 0 },
  // Tabs sitting in a control row — CustomFilterTabs, a touch tighter.
  tabItem: { paddingTop: "8px", paddingBottom: "10px", marginRight: "16px" },

  // Three equal columns, one job each (tasks | what to share | reply helper).
  // Stacked below lg, where the helper moves up so it's reachable fast.
  columns: {
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(3, minmax(0, 1fr))" },
    gap: "16px",
    alignItems: "start",
  },
  column: (stackedOrder) => ({ minWidth: 0, order: { xs: stackedOrder, lg: 0 } }),

  // Pill — same shape as the dashboard's delta pill.
  pill: (color, backgroundColor) => ({
    display: "inline-flex",
    alignItems: "center",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "18px",
    color,
    backgroundColor,
    borderRadius: "10px",
    padding: "0 8px",
    whiteSpace: "nowrap",
  }),

  primaryBtn: {
    backgroundColor: GREEN,
    color: "#fff",
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 500,
    padding: "6px 16px",
    boxShadow: "none",
    "&:hover": { backgroundColor: GREEN_HOVER, boxShadow: "none" },
    "&.Mui-disabled": { backgroundColor: GREEN, color: "#fff", opacity: 0.6 },
  },
  outlineBtn: {
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 500,
    color: TEXT_BODY,
    border: `1px solid ${INPUT_BORDER}`,
    backgroundColor: "#fff",
    padding: "4px 12px",
    minWidth: 0,
    whiteSpace: "nowrap",
    "& .MuiButton-startIcon": { marginInlineStart: 0, marginInlineEnd: "6px" },
    "&:hover": { backgroundColor: HOVER_BG, borderColor: GREEN_ACCENT },
  },
  // Small single-select option (answer tone, preview switcher).
  toggleChip: (active) => ({
    borderRadius: "10px",
    textTransform: "none",
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "18px",
    padding: "2px 10px",
    minWidth: 0,
    color: active ? GREEN : TEXT_BODY,
    border: `1px solid ${active ? GREEN : INPUT_BORDER}`,
    backgroundColor: active ? GREEN_TINT : "#fff",
    "&:hover": { backgroundColor: active ? GREEN_TINT : HOVER_BG, borderColor: active ? GREEN : GREEN_ACCENT },
  }),
  // Plain MUI TextField dressed like InputField (for inline fields).
  fieldSx: {
    "& .MuiInputBase-root": { borderRadius: "8px" },
    "& .MuiInputBase-input": { fontSize: "14px", color: TEXT_MAIN, padding: "6px 10px" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: INPUT_BORDER },
      "&:hover fieldset": { borderColor: GREEN_ACCENT },
      "&.Mui-focused fieldset": { borderColor: GREEN_ACCENT },
    },
  },
};
